import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Check } from "lucide-react";
import { Meta } from "../components/SEO";
import { serviceCategories, services } from "../data/services";
import { CtaStrip } from "../components/Sections";

function IsolatieCombiTegel({ light }) {
  const items = [
    { slug: "gevelisolatie", label: "Gevel isolatie", subsidy: "€ 20,25 – 40,50 / m²" },
    { slug: "dakisolatie", label: "Dakisolatie", subsidy: "€ 16,25 – 32,50 / m²" },
    { slug: "hr-plus-glas", label: "HR++ / Triple Glas", subsidy: "€ 25 – 50 / m² (HR++)" },
  ];
  return (
    <div
      className={`mb-10 rounded-2xl overflow-hidden border ${
        light
          ? "bg-gradient-to-br from-[#FBF8F0] via-white to-[#F5EBD8] border-[#D9C68A] shadow-premium"
          : "bg-gradient-to-br from-[#151515] via-[#1A1614] to-[#221B10] border-[#3A2E15]"
      }`}
      data-testid="isolatie-combi-tegel"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        <div className="p-8 md:p-10 lg:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-koning-gold/15 text-koning-gold text-[10px] tracking-[0.28em] uppercase mb-5">
            <Sparkles size={12} /> Combi-pakket · ISDE 2026
          </div>
          <h3 className={`font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] ${light ? "text-[#1A1816]" : "text-white"}`}>
            Combineer isolatie­maatregelen — <span className="italic text-koning-gold">maximale ISDE-subsidie</span>
          </h3>
          <p className={`mt-4 max-w-xl leading-relaxed ${light ? "text-[#5B564C]" : "text-koning-muted"}`}>
            De ISDE-regeling keert bij één isolatiemaatregel het lage tarief uit — combineert u binnen 24 maanden twee of meer maatregelen, dan verdubbelt het tarief per m². Wij rekenen vooraf persoonlijk door welke combinatie van gevel, dak en HR++ glas voor uw woning het meeste oplevert.
          </p>

          <ul className="mt-6 space-y-2.5">
            {[
              "Wij dienen de complete ISDE-aanvraag namens u in bij RVO",
              "9% BTW op arbeidskosten bij isolatie van woningen ouder dan 2 jaar",
              "Eén planning, één aannemer, één vaste totaalprijs",
              "Persoonlijke subsidie- en besparingsberekening vooraf",
            ].map((t) => (
              <li key={t} className={`flex items-start gap-2.5 text-sm ${light ? "text-[#3A362E]" : "text-white/80"}`}>
                <Check size={16} className="text-koning-gold shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/isolatie-combi"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-koning-gold text-[#1A1816] text-sm font-medium hover:bg-koning-gold/90 transition-colors"
              data-testid="combi-cta-offerte"
            >
              Bereken mijn combi-subsidie <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors ${
                light
                  ? "border-[#1A1816]/20 text-[#1A1816] hover:border-koning-gold hover:text-koning-gold"
                  : "border-white/20 text-white hover:border-koning-gold hover:text-koning-gold"
              }`}
              data-testid="combi-cta-contact"
            >
              Advies aanvragen
            </Link>
          </div>
        </div>

        <div className={`p-8 md:p-10 lg:p-12 border-t lg:border-t-0 lg:border-l ${light ? "border-[#D9C68A]/60 bg-white/40" : "border-[#3A2E15] bg-black/20"}`}>
          <div className={`text-[10px] tracking-[0.28em] uppercase mb-5 ${light ? "text-[#8A7430]" : "text-koning-gold"}`}>
            Onderdelen van het pakket
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <Link
                key={it.slug}
                to={`/diensten/${it.slug}`}
                className={`group flex items-center justify-between p-4 rounded-xl border transition-colors ${
                  light
                    ? "bg-white border-[#E1D9C5] hover:border-koning-gold hover:bg-[#FBF8F0]"
                    : "bg-koning-bg border-koning-border hover:border-koning-gold hover:bg-koning-surface"
                }`}
                data-testid={`combi-item-${it.slug}`}
              >
                <div className="flex items-center gap-4">
                  <span className={`font-display text-2xl ${light ? "text-koning-gold" : "text-koning-gold"}`}>
                    0{i + 1}
                  </span>
                  <div>
                    <div className={`font-medium text-base ${light ? "text-[#1A1816]" : "text-white"} group-hover:text-koning-gold transition-colors`}>
                      {it.label}
                    </div>
                    <div className={`text-xs mt-0.5 ${light ? "text-[#8A7430]" : "text-koning-muted"}`}>
                      ISDE-subsidie {it.subsidy}
                    </div>
                  </div>
                </div>
                <ArrowUpRight size={16} className={`${light ? "text-[#8A7430]" : "text-koning-gold"} group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform`} />
              </Link>
            ))}
          </div>
          <p className={`mt-5 text-xs leading-relaxed ${light ? "text-[#8A7C68]" : "text-koning-muted/70"}`}>
            * Officiële ISDE 2026-tarieven (RVO): het lage bedrag geldt bij 1 maatregel, het hoge bij 2+ maatregelen binnen 24 maanden. Exacte subsidie afhankelijk van geïsoleerd oppervlak en Rc-/U-waarde. Bedragen en voorwaarden kunnen tussentijds wijzigen — wij maken vooraf een persoonlijke berekening.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function DienstenPage() {
  React.useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, []);

  return (
    <>
      <Meta
        title="Diensten — Aannemer Maassluis & Zuid-Holland | Koning"
        description="Alle diensten van aannemer Koning: woningrenovatie, badkamer, zolder, aanbouw, kozijnen en isolatie. Vaste prijs en één aanspreekpunt — bekijk het overzicht."
      />
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 border-b border-koning-border" data-testid="diensten-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-5">Diensten</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl">
            Alles wat u nodig heeft, onder <span className="italic text-koning-gold">één dak</span>.
          </h1>
          <p className="mt-6 text-koning-muted max-w-2xl leading-relaxed">
            Van complete woningrenovaties tot losse vakgebieden — wij voeren projecten uit met een vast uitvoeringsteam en één centraal aanspreekpunt. Hieronder vindt u ons volledige aanbod.
          </p>
        </div>
      </section>

      {serviceCategories.map((cat, idx) => {
        const items = services.filter((s) => s.category === cat.slug);
        const light = idx % 2 === 0; // alternate light/dark per category
        const showCombi = cat.slug === "duurzaamheid-isolatie";
        return (
          <section
            key={cat.slug}
            id={cat.slug}
            className={light
              ? "py-20 md:py-24 bg-[#F5F2EA] border-b border-[#E1D9C5]"
              : "py-20 md:py-24 bg-[#0B0B0B] border-b border-koning-border"}
            data-testid={`diensten-category-${cat.slug}`}
          >
            <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                <div>
                  <div className={light ? "text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-3" : "eyebrow mb-3"}>Categorie</div>
                  <h2 className={`font-display text-3xl md:text-5xl tracking-tight ${light ? "text-[#1A1816]" : "text-white"}`}>{cat.title}</h2>
                  <p className={`mt-3 max-w-xl ${light ? "text-[#6F6A60]" : "text-koning-muted"}`}>{cat.intro}</p>
                </div>
              </div>

              {showCombi && <IsolatieCombiTegel light={light} />}

              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px ${light ? "bg-[#E1D9C5]" : "bg-koning-border"}`}>
                {items.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/diensten/${s.slug}`}
                    className={`group p-7 flex flex-col transition-colors ${
                      light
                        ? "bg-white hover:bg-[#FBF8F0] shadow-premium"
                        : "bg-koning-bg hover:bg-koning-surface"
                    }`}
                    data-testid={`diensten-service-${s.slug}`}
                  >
                    <h3 className={`font-display text-2xl transition-colors ${light ? "text-[#1A1816] group-hover:text-[#8A7430]" : "text-white group-hover:text-koning-gold"}`}>{s.title}</h3>
                    <p className={`mt-3 text-sm leading-relaxed flex-1 ${light ? "text-[#6F6A60]" : "text-koning-muted"}`}>{s.short}</p>
                    <div className="mt-5 flex items-center justify-between text-xs tracking-[0.22em] uppercase">
                      <span className={light ? "text-[#8A7430]" : "text-koning-gold"}>Offerte op maat</span>
                      <ArrowUpRight size={16} className={`${light ? "text-[#8A7430]" : "text-koning-gold"} group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform`} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <CtaStrip
        title="Niet gevonden wat u zoekt?"
        subtitle="Wij voeren ook losse opdrachten en specialistisch maatwerk uit. Vraag het ons gerust."
      />
    </>
  );
}
